import { useTranslations } from '@mentimeter/i18n';
import { userPolicies, useUser } from '@mentimeter/user';
import {
  getPlansPageConfig,
  type PlansPageConfigT,
} from '@mentimeter/plans-content';
import { Link } from 'src/ui/actions';
import {
  DEFAULT_PRICE,
  useSubscriptions,
  useUpdateOptions,
} from '@mentimeter/billing';
import { trackVisitor } from 'src/trackVisitor';
import { useEngagementLimits } from '@mentimeter/paywalls-data-hooks';
import { trackUser } from '@api/tracking/client';
import { useCallback, useEffect, useState } from 'react';
import { useMonthlyBilling, usePrices } from '@mentimeter/plans-ui';
import { getVisitorDataCache } from '@mentimeter/visitor/visitor-data';
import { useFlag } from '@mentimeter/flags/confidence/client';
import { useTrackPlan } from './useTrackPlan';

export const usePlansConfig = ({ isEducation }: { isEducation: boolean }) => {
  const [monthlyToggled, setMonthlyToggled] = useState(false);
  const { user } = useUser();
  const isFreeUser = user?.isFreeUser ?? false;
  const { subscriptions } = useSubscriptions(!user);
  const { country, preferredLanguage } = getVisitorDataCache();
  const t = useTranslations('plansContentPackage');
  const isLoggedIn = Boolean(user);
  const { monthlyBillingIsSupported } = useMonthlyBilling();

  const engagementLimits = useEngagementLimits();
  const { isBasicMonthlyUser } = userPolicies(subscriptions, user);

  const { value: showValueOrientedUsps } = useFlag(
    'pug-value-oriented-pricing-page.enabled',
    false,
  );

  const {
    prices: dynamicPrices,
    error: priceError,
    isLoading: isPriceLoading,
  } = usePrices();
  const { possibleUpdates } = useUpdateOptions(!user);
  const showDynamicPrices = !priceError && !isPriceLoading;

  const { trackPlan } = useTrackPlan(isBasicMonthlyUser);

  const toggleSwitch = useCallback(() => {
    const payload = {
      event: 'Clicked billing toggle',
      properties: {
        context: 'Plans page',
        cycle: monthlyToggled ? 'yearly' : 'monthly',
      },
    };
    if (user) {
      trackUser(payload);
    } else {
      trackVisitor(payload);
    }
    setMonthlyToggled((value) => !value);
  }, [user, monthlyToggled]);

  const [config, setConfig] = useState<PlansPageConfigT>(
    getPlansPageConfig({
      isEducation,
      isFreeUser: false,
      loggedIn: isLoggedIn,
      userPolicies: userPolicies(subscriptions, user),
      prices:
        showDynamicPrices && dynamicPrices ? dynamicPrices : DEFAULT_PRICE,
      isPriceLoading,
      monthlyToggled,
      possibleUpdates,
      monthlyBillingIsSupported,
      toggleSwitch,
      userEngagementLimitProgress:
        engagementLimits?.engagementData.progress ?? undefined,
      host: '', // Note 2022-08-31: Buttons in content cannot handle absolute URLs, and require relative URLs even for redirecting to another application.
      Link,
      trackPlan,
      t,
      country,
      showValueOrientedUsps,
    }),
  );

  useEffect(() => {
    setConfig(
      getPlansPageConfig({
        isEducation,
        isFreeUser,
        loggedIn: isLoggedIn,
        userPolicies: userPolicies(subscriptions, user),
        prices:
          showDynamicPrices && dynamicPrices ? dynamicPrices : DEFAULT_PRICE,
        isPriceLoading,
        monthlyToggled,
        possibleUpdates,
        monthlyBillingIsSupported,
        toggleSwitch,
        userEngagementLimitProgress:
          engagementLimits?.engagementData.progress ?? undefined,
        host: '', // Note 2022-08-31: Buttons in content cannot handle absolute URLs, and require relative URLs even for redirecting to another application.
        Link,
        trackPlan,
        t,
        country,
        showValueOrientedUsps,
      }),
    );
  }, [
    user,
    monthlyToggled,
    possibleUpdates,
    preferredLanguage,
    country,
    isEducation,
    isFreeUser,
    t,
    trackPlan,
    toggleSwitch,
    monthlyBillingIsSupported,
    isLoggedIn,
    dynamicPrices,
    priceError,
    isPriceLoading,
    engagementLimits?.engagementData.progress,
    subscriptions,
    showDynamicPrices,
    showValueOrientedUsps,
  ]);

  return config;
};
